import { defineStore } from 'pinia';
import i18n from '@/plugins/vue-i18n';
import { analyticsService } from '@/plugins/post-hog';
import { useToastStore } from '@/stores/utils/toast.store';
import {
  AddProjectUserBody,
  BOQSummary,
  Cashflow,
  CashflowGraph,
  CashflowParams,
  CreateProjectBody,
  Project,
  ProjectDates,
  ProjectImageBody,
  ProjectUserRole,
  UpdateProjectBody,
} from '@/types/api/project.types';
import {
  addUsers,
  createProject,
  deleteImage,
  exportProjectCashflow,
  getProject,
  getProjectBOQSummaries,
  getProjectCashflow,
  getProjectCashflowGraph,
  getProjectDates,
  removeUser,
  updateProject,
  updateProjectClaimBannerDisplay,
  uploadImage,
  validateErfNumber,
} from '@/api/project.service';
import { getOrganisationProjects } from '@/api/organisations.service';

import { ProjRoles } from '@/types/api/roles.types';
import { isSuccess } from '@/common/utils/http.utils';
import { useOrgStore } from '@/stores/organisations.store';
import { ProjectParams } from '@/types/api/organisations.types';
import { getFutureMonthIndexes } from '@/common/utils/site.utils';
import { ErfNumberValidationParams } from '@/types/api/sites.types';
import { exportFile, documentUploadToast } from '@/common/utils/general.utils';
import { RoleCode, selectedOrgId, sortRolesByOrder } from '@/common/utils/role.utils';

const { t } = i18n.global;

export const useProjectStore = defineStore('project', {
  state: () => ({
    project: {} as Project,
    newlyCreatedProject: {} as Project,
    projectDates: {} as ProjectDates,
    projectCashflow: {} as Cashflow,
    projectCashflowGraphData: {} as CashflowGraph,
    projectCashflowFutureMonthIndexes: [] as number[],
    boqSummaries: [] as BOQSummary[],
    exportedCashflow: null as Blob | null,
    isErfNumberAvailable: null as boolean | null,

    // Results for the org-wide project search used by the autocomplete dropdowns
    // (e.g. central create order/claim). Empty search holds the first page.
    searchedProjects: [] as Project[],

    // looking to check if there are any sites with approved boqs within the project
    projectParams: {
      orderBy: 'Name',
      status: 'Approved',
    } as ProjectParams,
  }),

  actions: {
    async createProject(data: CreateProjectBody) {
      const orgStore = useOrgStore();
      const toastStore = useToastStore();
      const response = await createProject(data);
      orgStore.allProjects = [...orgStore.allProjects, response.data.data];

      this.project = response.data.data;

      if (isSuccess(response.status)) {
        toastStore.showToast(
          'success',
          t('sites.createProjectSuccess.title'),
          t('sites.createProjectSuccess.content'),
        );

        analyticsService.capture('project_created', {
          org_id: selectedOrgId.value!,
          project_id: this.project.id,
          project_name: this.project.name,
        });
      }

      this.newlyCreatedProject = response.data.data;
    },

    async getProject(id: string) {
      const orgStore = useOrgStore();
      const response = await getProject(id);
      const project = response.data.data;

      if (project.projectUserRoles && project.projectUserRoles.length > 0) {
        project.projectUserRoles = sortRolesByOrder('project', project.projectUserRoles, {
          getRoleCode: (user: ProjectUserRole) => user.projectRole.code as RoleCode,
          getPendingStatus: (user: ProjectUserRole) => user.pendingInvite,
          userExistsOnPlatform: (user: ProjectUserRole) => !!user.firstName,
        });
      }

      this.project = project;

      orgStore.updateProjectDataInList(this.project);
    },

    async updateProject(id: string, data: UpdateProjectBody) {
      const toastStore = useToastStore();

      const response = await updateProject(id, data);

      if (isSuccess(response.status)) {
        toastStore.showToast(
          'success',
          t('projects.editProject.updateSuccess.title'),
          t('projects.editProject.updateSuccess.content'),
        );
      }
    },

    async uploadImage(data: ProjectImageBody, options?: { silent?: boolean }) {
      const response = await uploadImage(data);

      documentUploadToast({
        responseCode: response.status,
        title: t('projects.editProject.imageUploadSuccess.title'),
        description: t('projects.editProject.imageUploadSuccess.content'),
        showToast: !options?.silent,
        // Unlike the vendor endpoint, this one returns only the image, not the full project —
        // patch just `profileImage` so callers reading `project.profileImage.url` see it fresh.
        onSuccess: async () => {
          this.project = {
            ...this.project,
            profileImage: { url: response.data.data.url, expiresAt: response.data.data.expiry },
          };
        },
      });
    },

    async deleteImage(id: string, options?: { silent?: boolean }) {
      const toastStore = useToastStore();

      const response = await deleteImage(id);

      if (isSuccess(response.status) && !options?.silent) {
        toastStore.showToast(
          'success',
          t('projects.editProject.imageDeleteSuccess.title'),
          t('projects.editProject.imageDeleteSuccess.content'),
        );
      }
    },

    async addUsers(id: string, data: AddProjectUserBody[], options?: { skipToast?: boolean }) {
      const toastStore = useToastStore();

      const response = await addUsers(id, data);

      const added = isSuccess(response.status);

      if (added && !options?.skipToast) {
        toastStore.showToast(
          'success',
          t('projects.editProject.userAddedSuccess.title'),
          t('projects.editProject.userAddedSuccess.content'),
        );
      }

      return added;
    },

    async removeUser(
      userId: string,
      projectRoleId: string,
      roleCode?: ProjRoles,
      options?: { skipToast?: boolean },
    ) {
      const orgStore = useOrgStore();
      const toastStore = useToastStore();

      const response = await removeUser(projectRoleId);
      const removed = isSuccess(response.status);

      if (removed) {
        this.project = {
          ...this.project,
          projectUserRoles: (this.project.projectUserRoles ?? []).filter(
            (u) => !(u.userId === userId && u.projectRole.code === roleCode),
          ),
        };

        orgStore.updateProjectDataInList(this.project);
      }

      if (removed && !options?.skipToast) {
        toastStore.showToast(
          'success',
          t('projects.editProject.userRemovedSuccess.title'),
          t('projects.editProject.userRemovedSuccess.content'),
        );
      }

      return removed;
    },

    async getProjectDates(id: string) {
      const response = await getProjectDates(id);
      this.projectDates = response.data.data;
    },

    async getProjectCashflow(id: string, params: CashflowParams) {
      const response = await getProjectCashflow(id, params);
      this.projectCashflow = response.data.data;
    },

    async getProjectCashflowGraph(id: string, params: CashflowParams) {
      const response = await getProjectCashflowGraph(id, params);
      this.projectCashflowGraphData = response.data.data;
      this.projectCashflowFutureMonthIndexes = getFutureMonthIndexes(
        this.projectCashflowGraphData.cumulativeNet,
      );
    },

    async updateProjectClaimBannerDisplay(id: string) {
      const response = await updateProjectClaimBannerDisplay(id);
      this.project = response.data.data;
    },

    async getBOQSummaries(params: ProjectParams) {
      const response = await getProjectBOQSummaries({ ...this.projectParams, ...params });

      this.boqSummaries = response.data.data;
    },

    resetBoqSummaries() {
      this.boqSummaries = [];
    },

    async exportProjectCashflow(id: string, projectName: string) {
      const toastStore = useToastStore();

      try {
        const response = await exportProjectCashflow(id);
        const contentType =
          (response.headers['content-type'] as string) ?? 'application/octet-stream';

        this.exportedCashflow = new Blob([response.data], {
          type: contentType,
        });

        exportFile(this.exportedCashflow, contentType, `${projectName} - Cashflow`);

        toastStore.showToast(
          'success',
          t('projects.cashflow.exportCashflow.exportSuccess.title'),
          t('projects.cashflow.exportCashflow.exportSuccess.content', { name: projectName }),
        );
      } catch (error) {
        console.error('Error downloading the file:', error);
      }
    },

    setSelectedProject(project: Project) {
      this.project = project;
    },

    // Searches every project in the org. An empty search returns the first page of projects.
    async searchProjects(search: string, archived = false) {
      const response = await getOrganisationProjects(selectedOrgId.value!, {
        pageSize: 10,
        archived,
        search,
      });

      this.searchedProjects = response.data.data;
    },

    // Ensures a project is present in the search results so the autocomplete can
    // display it — used when a site is picked whose project is not on the current page.
    // Returns the project so the caller can select it in the field.
    async addProjectToSearchResults(id: string): Promise<Project> {
      const existing = this.searchedProjects.find((project) => project.id === id);

      if (existing) {
        return existing;
      }

      const response = await getProject(id);
      this.searchedProjects = [response.data.data, ...this.searchedProjects];

      return response.data.data;
    },

    async validateErfNumber(params: ErfNumberValidationParams) {
      const response = await validateErfNumber({ ...params });

      this.isErfNumberAvailable = response.data.data;
      return this.isErfNumberAvailable;
    },
  },
});
